// Session media — module-level singleton that keeps the webcam + mic stream
// AND the MediaRecorder alive across route changes, so the candidate grants
// camera/mic permission exactly ONCE (on Environment Check) and the
// recording runs continuously until they submit the test.
//
// MONOLITHIC RECORDING (Apr 2026):
// We deliberately do NOT use MediaRecorder timeslicing anymore. The chunked
// approach (timeslice=5000, /recording-chunk + /recording-finalize) was
// silently truncating candidate recordings to 14-30s during Object Storage
// outages because chunk uploads queued up and timed out. Worse, byte-
// concatenating MediaRecorder timeslice outputs isn't reliably playable in
// every browser.
//
// The new flow is: one MediaRecorder running from Environment Check until
// submit, holding the full session in memory (~60 MB for 30 min at 250 kbps
// / 640×360). On submit the blob is uploaded once via /recording (monolithic).
// That's it. Simple, reliable, plays in every browser.

import { pickMime, stopAllTracks } from "./media";

let _camStream = null;
let _webcamRecorder = null;   // MediaRecorder instance (NOT ChunkedRecorder)
let _chunks = [];             // accumulated Blobs captured via ondataavailable
let _started = false;

export function setSessionCamStream(stream) {
  _camStream = stream;
}

export function getSessionCamStream() {
  return _camStream;
}

/**
 * Start a session-long MediaRecorder with NO timeslicing. `ondataavailable`
 * only fires on stop(), so `_chunks` ends up containing a single Blob with
 * the entire session. Idempotent.
 */
export function startSessionWebcamRecording() {
  if (_started && _webcamRecorder) return _webcamRecorder;
  if (!_camStream) return null;

  _chunks = [];
  const mime = pickMime(true);
  const opts = {};
  if (mime) opts.mimeType = mime;
  // 250 kbps × 30 min = ~56 MB — comfortably fits in memory and uploads
  // in under a minute even on poor Wi-Fi. `videoBitsPerSecond` is ignored
  // by some implementations but honored on Chromium / Edge which cover
  // 100% of our supported browsers (Safari/Firefox are blocked upstream).
  opts.videoBitsPerSecond = 250_000;

  try {
    _webcamRecorder = new MediaRecorder(_camStream, Object.keys(opts).length ? opts : undefined);
  } catch (_e) {
    // Fallback to browser defaults if the requested mime/bitrate isn't supported.
    _webcamRecorder = new MediaRecorder(_camStream);
  }
  _webcamRecorder.ondataavailable = (ev) => {
    if (ev.data && ev.data.size > 0) _chunks.push(ev.data);
  };
  _webcamRecorder.start();   // NO timeslice → one big blob on stop()
  _started = true;
  return _webcamRecorder;
}

export function getSessionWebcamRecorder() {
  return _webcamRecorder;
}

export function hasSessionWebcamRecording() {
  return !!_started;
}

/**
 * Stop the recorder and return the final blob (the entire session).
 * Caller uploads it via POST /student/attempt/{id}/recording (monolithic).
 */
export async function stopSessionWebcamRecording() {
  return new Promise((resolve) => {
    if (!_webcamRecorder) return resolve(null);
    const rec = _webcamRecorder;
    _webcamRecorder = null;
    _started = false;
    rec.onstop = () => {
      const blob = new Blob(_chunks, { type: rec.mimeType || "video/webm" });
      _chunks = [];
      resolve(blob);
    };
    try {
      if (rec.state !== "inactive") rec.stop();
      else resolve(new Blob(_chunks, { type: rec.mimeType || "video/webm" }));
    } catch (_e) {
      resolve(new Blob(_chunks, { type: rec.mimeType || "video/webm" }));
    }
  });
}

export function releaseSessionMedia() {
  stopAllTracks(_camStream);
  _camStream = null;
  _webcamRecorder = null;
  _chunks = [];
  _started = false;
}

// Legacy exports kept as no-ops so older imports don't error.
// Remove once no caller references them.
export function getSessionWebcamChunkUploader() { return null; }
